import React, { Component } from "react";
import { withTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { toLocalHref } from "../../shared/clientLocale";
import {
  PRODUCT_ROUTES,
  getRedesignContent,
  isRtlLanguage,
  normalizeRedesignLanguage
} from "../../redesign/content";
import { filterProductRoutesForLanguage } from "../../shared/productLocalePolicy";
import { getStaticRoutePaths } from "../../shared/staticRoutePaths";
import { getTrademarkNotice } from "../../shared/redesignTemplateRules";
import { OutlineIcon } from "../../redesign/RedesignIcons";

// Kênh chính thức — cùng danh sách với sameAs trong JSON-LD Organization (workers/vi/index.js).
const SOCIAL_LINKS = [
  { href: "https://www.facebook.com/Ninecode.jsc", label: "Facebook" },
  { href: "https://www.youtube.com/@Qrcode-gen", label: "YouTube" }
];

class Footer extends Component {
  renderLinkList(items, className = "") {
    return (
      <ul className={`footer-list ${className}`}>
        {items.map((item) => (
          <li className="footer-item" key={`${item.to}-${item.label}`}>
            <a className="footer-link" href={toLocalHref(item.to)}>
              {item.label}
              <span aria-hidden="true">→</span>
            </a>
          </li>
        ))}
      </ul>
    );
  }

  renderExternalLinkList(items) {
    return (
      <ul className="footer-list">
        {items.map((item) => (
          <li className="footer-item" key={item.href}>
            <a
              className="footer-link"
              href={item.href}
              target="_blank"
              rel="noopener noreferrer"
            >
              {item.label}
              <span aria-hidden="true">↗</span>
            </a>
          </li>
        ))}
      </ul>
    );
  }

  render() {
    const { t, i18n } = this.props;
    const language = i18n.language;
    const normalizedLanguage = normalizeRedesignLanguage(language);
    const redesignContent = getRedesignContent(language);
    const staticRoutes = getStaticRoutePaths(language);
    const isWhatIsQrPage =
      typeof window !== "undefined" &&
      ["/what-is-qr-code", "/qr-code-la-gi"].includes(window.location.pathname);
    const productLinks = filterProductRoutesForLanguage(
      PRODUCT_ROUTES,
      language
    ).map((route) => ({
      to: route.path,
      label: redesignContent.pages[route.key].navLabel
    }));
    const guideLinks = [
      { to: "/", label: t("navbar.menu-right.homepage") },
      { to: staticRoutes.whatIsQr, label: t("navbar.menu-right.what-is-qrcode") },
      { to: staticRoutes.servicePack, label: t("navbar.menu-right.package") },
      { to: "/document", label: t("navbar.menu-right.document") },
      { to: "/use-cases", label: t("navbar.menu-right.use-case") },
      { to: "/blog", label: t("navbar.menu-right.news") },
      { to: "/#faq", label: redesignContent.common.faq }
    ];
    const legalLinks = [
      { to: staticRoutes.terms, label: t("navbar.menu-right.term-of-use") },
      { to: staticRoutes.privacy, label: t("footer.usage-policy") },
      { to: staticRoutes.contact, label: t("footer.contact-support") }
    ];
    const otherProductLinks = [
      { href: "https://menu.nineqr.com", label: t("footer.qr-code-menu") },
      { href: "https://asset.nineqr.com", label: t("footer.qr-code-asset") },
      { href: "https://www.ninequiz.com/", label: t("footer.ninequiz") },
      { href: "https://ninecode.vn/", label: t("footer.software-consultant") },
      { href: "https://ninecard.one/", label: t("footer.ninecard") },
      { href: "https://ninetick.com", label: t("footer.ninetick") }
    ];

    return (
      <footer
        className="footer-container"
        dir={isRtlLanguage(language) ? "rtl" : "ltr"}
      >
        <div className="footer-content">
          <div className="footer-main">
            <div className="footer-brand-column">
              <a className="footer-brand" href={toLocalHref("/")} aria-label="QRCode-Gen">
                <img
                  src="/images/logo-qrcode-gen-web.png"
                  alt=""
                  width="40"
                  height="32"
                />
                <span>QRCode-Gen</span>
              </a>
              <p className="footer-brand-description">
                {redesignContent.home.cta.description}
              </p>
              <div className="footer-contact-list">
                {language === "vi" && (
                  <a href="tel:0868685247">
                    <OutlineIcon name="mobile" size={15} className="footer-contact-icon" />
                    <span>{t("footer.contact-phone")}</span>
                  </a>
                )}
                <a href="mailto:info@ninecode.vn">
                  <OutlineIcon name="mail" size={15} className="footer-contact-icon" />
                  <span>{t("footer.contact-email")}</span>
                </a>
                {language === "vi" && (
                  <address>
                    <OutlineIcon name="location" size={15} className="footer-contact-icon" />
                    <span>{t("footer.address")}</span>
                  </address>
                )}
              </div>
              <div className="footer-social">
                {SOCIAL_LINKS.map((item) => (
                  <a
                    key={item.href}
                    href={item.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={`QRCode-Gen ${item.label}`}
                  >
                    <span>{item.label}</span>
                    <span aria-hidden="true">↗</span>
                  </a>
                ))}
              </div>
            </div>

            <nav className="footer-col" aria-label={redesignContent.nav.products}>
              <h2 className="footer-heading">{redesignContent.nav.products}</h2>
              {this.renderLinkList(productLinks, "footer-product-links")}
            </nav>

            <nav className="footer-col" aria-label={t("footer.guide")}>
              <h2 className="footer-heading">{t("footer.guide")}</h2>
              {this.renderLinkList(guideLinks)}
            </nav>

            <nav className="footer-col" aria-label={t("footer.term")}>
              <h2 className="footer-heading">{t("footer.term")}</h2>
              {this.renderLinkList(legalLinks)}
            </nav>

            <nav className="footer-col" aria-label={t("footer.other-product")}>
              <h2 className="footer-heading">{t("footer.other-product")}</h2>
              {this.renderExternalLinkList(otherProductLinks)}
            </nav>
          </div>

          <div className="footer-bottom">
            <div>
              <p>
                © {new Date().getFullYear()} QRCode-Gen.{" "}
                {t("footer.other-product-ninecode")}.
              </p>
              {!isWhatIsQrPage && (
                <p className="footer-trademark-notice">
                  {getTrademarkNotice(
                    normalizedLanguage,
                    redesignContent.common.trademarkNotice
                  )}
                </p>
              )}
            </div>
            <div className="footer-bottom-links">
              <a href={staticRoutes.privacy}>{t("footer.usage-policy")}</a>
              <a href={toLocalHref(staticRoutes.contact)}>{t("navbar.menu-right.contact")}</a>
            </div>
          </div>
        </div>
      </footer>
    );
  }
}

export default withTranslation()(Footer);
